import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, styled } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import { SapContractFeeTableView } from '@/components/Approval/@components/SapContractFee';
import { getSapContractFeeDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildSapContractFeeYears,
  checkSapContractFeeQuery,
  createSapContractFeeState,
  findSapContractFeeBrandLabel,
  getSapContractFeeItems,
  SAP_CONTRACT_FEE_BRANDS,
  SAP_CONTRACT_FEE_MONTHS,
  SAP_CONTRACT_FEE_NO_DATA_MESSAGE,
  type SapContractFeeDraftSource,
  type SapContractFeeState,
} from '@/utils/approval/sapContractFee';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const QueryRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const selectSx = { ...approvalOutlineSelectSx, minWidth: 140 };

/** SAP 도급비 수수료 정산(167) — 레거시 appr_edit_forms_pay_contract_fee */
const SapContractFeeField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state =
    useWatch({ control, name: 'sapContractFee' }) ??
    createSapContractFeeState();
  const [loading, setLoading] = useState(false);
  // 조회 중 조건을 바꾸면 늦게 온 이전 조건 응답이 표·제목을 덮지 않게 무효 처리
  const requestIdRef = useRef(0);
  const years = useMemo(() => buildSapContractFeeYears(), []);

  const update = (patch: Partial<SapContractFeeState>) =>
    setValue(
      'sapContractFee',
      {
        ...(getValues('sapContractFee') ?? createSapContractFeeState()),
        ...patch,
      },
      { shouldDirty: true },
    );

  const handleLoad = async () => {
    if (loading) return;
    const current = getValues('sapContractFee') ?? createSapContractFeeState();
    const violation = checkSapContractFeeQuery(current);
    if (violation) {
      snackbar.showError({ message: t(violation) });
      return;
    }
    const { brand, year, month } = current;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    try {
      const source = await getNestedDataFetcher<SapContractFeeDraftSource>(
        getSapContractFeeDraftSourceUrl(brand, year, month),
      );
      if (requestId !== requestIdRef.current) return;
      const loaded = source.attributes;
      update({ loaded });
      if (getSapContractFeeItems(loaded.data).length > 0) {
        setValue(
          'subject',
          t('{{brand}} {{year}}년 {{month}}월 도급 수수료 정산의 건', {
            brand: t(findSapContractFeeBrandLabel(brand)),
            year,
            month,
          }),
          { shouldDirty: true },
        );
      } else {
        setValue('subject', '', { shouldDirty: true });
        snackbar.showError({ message: t(SAP_CONTRACT_FEE_NO_DATA_MESSAGE) });
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      update({ loaded: null });
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const changeQuery = (patch: Partial<SapContractFeeState>) => {
    requestIdRef.current += 1;
    setLoading(false);
    update(patch);
  };

  return (
    <FieldBlock className="sap-contract-fee">
      <QueryRow>
        <Select
          value={state.brand}
          placeholder={t('브랜드 선택')}
          options={SAP_CONTRACT_FEE_BRANDS.map((b) => ({
            value: b.value,
            label: t(b.label),
          }))}
          onChange={(e) => changeQuery({ brand: e.target.value as string })}
          sx={selectSx}
        />
        <Select
          value={state.year}
          placeholder={t('연도 선택')}
          options={years.map((y) => ({ value: y, label: y }))}
          onChange={(e) => changeQuery({ year: e.target.value as string })}
          sx={selectSx}
        />
        <Select
          value={state.month}
          placeholder={t('월 선택')}
          options={SAP_CONTRACT_FEE_MONTHS.map((m) => ({
            value: m,
            label: m,
          }))}
          onChange={(e) => changeQuery({ month: e.target.value as string })}
          sx={selectSx}
        />
        <BoxButton
          variant="secondary"
          disabled={loading}
          startIcon={
            loading ? <Spinner theme="black" size="16px" /> : undefined
          }
          onClick={handleLoad}
        >
          {t('데이터 호출')}
        </BoxButton>
      </QueryRow>
      <SapContractFeeTableView data={state.loaded?.data ?? null} t={t} />
    </FieldBlock>
  );
};

export default SapContractFeeField;
